import { useEffect, useState, useRef, createContext, useContext } from "react";
import "@/App.css";
import axios from "axios";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { 
  Zap, Target, Layers, ArrowRight, Menu, X, Rocket, Brain, TrendingUp, Database,
  MessageSquare, Heart, Send, ExternalLink, ChevronDown, Check, AlertCircle,
  LogIn, LogOut, User, Lock, Mail, Building, Briefcase, Star, Clock, Sparkles, Cpu,
  Shield, Cloud, Server, Settings, Users, FileCheck
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("ezmq_token");
    if (token) {
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setUser(res.data))
        .catch(() => localStorage.removeItem("ezmq_token"))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("ezmq_token", res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (name, email, password, company) => {
    const res = await axios.post(`${API}/auth/register`, { name, email, password, company });
    localStorage.setItem("ezmq_token", res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem("ezmq_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Animation Variants
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const staggerItem = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

// Section Component
const Section = ({ children, className = "", id = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.section id={id} ref={ref} initial="hidden" animate={isInView ? "visible" : "hidden"} variants={fadeUp} className={className}>
      {children}
    </motion.section>
  );
};

// Auth Modal
const AuthModal = ({ isOpen, onClose, initialMode = "login" }) => {
  const [mode, setMode] = useState(initialMode);
  const [formData, setFormData] = useState({ name: "", email: "", password: "", company: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(formData.email, formData.password);
      } else {
        await register(formData.name, formData.email, formData.password, formData.company);
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center modal-overlay" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="modal-content w-full max-w-md mx-4 p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-heading text-xl font-bold">{mode === "login" ? "Client Sign In" : "Create Account"}</h2>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === "register" && (
            <>
              <div>
                <label className="text-xs uppercase tracking-wider text-white/50 mb-1 block">Name</label>
                <div className="relative">
                  <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your name"
                    className="w-full pl-10 pr-4 py-3 bg-black/50 border border-white/10 rounded-lg text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-white/50 mb-1 block">Company (optional)</label>
                <div className="relative">
                  <Building size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
                  <input
                    type="text"
                    value={formData.company}
                    onChange={e => setFormData({ ...formData, company: e.target.value })}
                    placeholder="Your company"
                    className="w-full pl-10 pr-4 py-3 bg-black/50 border border-white/10 rounded-lg text-sm"
                  />
                </div>
              </div>
            </>
          )}
          <div>
            <label className="text-xs uppercase tracking-wider text-white/50 mb-1 block">Email</label>
            <div className="relative">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
              <input
                type="email"
                required
                value={formData.email}
                onChange={e => setFormData({ ...formData, email: e.target.value })}
                placeholder="your@email.com"
                className="w-full pl-10 pr-4 py-3 bg-black/50 border border-white/10 rounded-lg text-sm"
              />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/50 mb-1 block">Password</label>
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
              <input
                type="password"
                required
                value={formData.password}
                onChange={e => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
                className="w-full pl-10 pr-4 py-3 bg-black/50 border border-white/10 rounded-lg text-sm"
              />
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2 text-red-400 text-sm">
              <AlertCircle size={16} /> {error}
            </div>
          )}

          <button type="submit" disabled={loading} className="btn-primary w-full py-3 text-sm flex items-center justify-center gap-2">
            {loading ? "..." : mode === "login" ? "Sign In" : "Create Account"}
          </button>
        </form>

        <p className="text-center text-sm text-white/50 mt-4">
          {mode === "login" ? "Don't have an account? " : "Already have an account? "}
          <button onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-[#2E5CFF] hover:underline">
            {mode === "login" ? "Sign up" : "Sign in"}
          </button>
        </p>
      </motion.div>
    </div>
  );
};

// Header
const Header = ({ currentPage, setCurrentPage }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const { user, logout } = useAuth();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = currentPage === "home" 
    ? [{ label: "Model", href: "#model" }, { label: "Enterprise", href: "#enterprise" }, { label: "Focus", href: "#focus" }, { label: "Portfolio", href: "#", onClick: () => setCurrentPage("portfolio") }, { label: "Contact", href: "#contact" }]
    : [{ label: "Home", href: "#", onClick: () => setCurrentPage("home") }];

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? "header-glass" : "bg-transparent"}`} data-testid="header">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <button onClick={() => setCurrentPage("home")} className="flex items-center gap-2" data-testid="logo-link">
              <div className="status-indicator"></div>
              <span className="font-heading text-lg font-bold">EZMQ</span>
              <span className="hidden md:block font-mono text-[10px] text-white/50 border-l border-white/20 pl-2 ml-1">Venture OS</span>
            </button>

            <nav className="hidden md:flex items-center gap-6" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <a key={link.label} href={link.href} onClick={link.onClick} className="text-sm text-white/60 hover:text-white transition-colors">
                  {link.label}
                </a>
              ))}
              {user ? (
                <div className="flex items-center gap-3">
                  <span className="text-sm text-white/60">{user.name}</span>
                  <button onClick={logout} className="btn-secondary px-4 py-2 text-xs flex items-center gap-1">
                    <LogOut size={14} /> Sign Out
                  </button>
                </div>
              ) : (
                <button onClick={() => setAuthModalOpen(true)} className="btn-primary px-5 py-2 text-sm flex items-center gap-2" data-testid="nav-signin">
                  <LogIn size={14} /> Client Sign In
                </button>
              )}
            </nav>

            <button className="md:hidden p-2 text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
              {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="mobile-menu md:hidden border-t border-white/5">
              <nav className="flex flex-col p-6 gap-3">
                {navLinks.map((link) => (
                  <a key={link.label} href={link.href} onClick={() => { link.onClick?.(); setMobileMenuOpen(false); }} className="text-white/70 hover:text-white py-2">{link.label}</a>
                ))}
                {user ? (
                  <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="btn-secondary px-5 py-3 text-sm mt-2">Sign Out</button>
                ) : (
                  <button onClick={() => { setAuthModalOpen(true); setMobileMenuOpen(false); }} className="btn-primary px-5 py-3 text-sm mt-2">Client Sign In</button>
                )}
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
      <AuthModal isOpen={authModalOpen} onClose={() => setAuthModalOpen(false)} />
    </>
  );
};

// Hero Section
const HeroSection = ({ setCurrentPage }) => (
  <section className="relative min-h-[90vh] flex items-center pt-16 hero-glow grid-pattern" data-testid="hero-section">
    <div className="relative z-10 max-w-6xl mx-auto px-6 py-20">
      <div className="max-w-3xl">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-5">
          <span className="section-label inline-flex items-center gap-2">
            <span className="w-2 h-2 bg-[#2E5CFF] rounded-full"></span>
            Venture Studio / Product Incubator / OS Builder
          </span>
        </motion.div>

        <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-[1.1]" data-testid="hero-title">
          Build Fast. Learn Faster. <span className="text-[#2E5CFF]">Scale What Works.</span>
        </motion.h1>

        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="text-lg text-white/60 max-w-2xl mb-8 leading-relaxed" data-testid="hero-subtitle">
          EZMQ is a venture studio building the future of work—and a consulting team that helps enterprises get there. We ship real products, transform services into scalable systems, and bring the same technical rigor to enterprise IT lifecycle management.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="flex flex-col sm:flex-row gap-3">
          <button onClick={() => setCurrentPage("portfolio")} className="btn-primary px-6 py-3 text-sm inline-flex items-center justify-center gap-2" data-testid="hero-cta-primary">
            <Briefcase size={16} /> View Portfolio
          </button>
          <a href="#contact" className="btn-secondary px-6 py-3 text-sm inline-flex items-center justify-center gap-2" data-testid="hero-cta-secondary">
            Start a Conversation
          </a>
        </motion.div>
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }} className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center">
        <motion.div animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.5 }}>
          <ChevronDown size={20} className="text-white/30" />
        </motion.div>
      </motion.div>
    </div>
  </section>
);

// What We Do
const WhatWeDoSection = () => {
  const principles = [
    { icon: <Zap size={20} />, label: "Speed beats perfection" },
    { icon: <Target size={20} />, label: "Validation beats opinion" },
    { icon: <Layers size={20} />, label: "Systems beat heroics" }
  ];

  return (
    <Section className="py-20 md:py-28 bg-[#0A0A0A]" id="what-we-do">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="section-label mb-4 block">What We Do</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-5">Ideas Into Working Products</h2>
            <p className="text-white/60 text-base leading-relaxed mb-6">
              EZMQ turns ideas into working products—quickly, deliberately, and with data. Each project starts as a lightweight MVP. If it proves traction, it graduates. If it doesn&apos;t, we extract the learning and move on.
            </p>
            <p className="font-mono text-sm text-[#2E5CFF]">Portfolio thinking, applied to software.</p>
          </div>
          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} className="space-y-3">
            {principles.map((p, i) => (
              <motion.div key={i} variants={staggerItem} className="card-dark p-5 flex items-center gap-4" data-testid={`principle-${i}`}>
                <div className="text-[#2E5CFF]">{p.icon}</div>
                <span className="font-mono text-sm uppercase tracking-wider">{p.label}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </Section>
  );
};

// Model Section
const ModelSection = () => {
  const steps = [
    { number: "01", title: "Rapid MVP Incubation", description: "We design and launch focused MVPs in weeks, not quarters. Each product solves one clear problem for one clear audience." },
    { number: "02", title: "Signal-Driven Evolution", description: "Usage, retention, and willingness to pay determine next steps. Emotion and ego do not get a vote." },
    { number: "03", title: "Graduation or Integration", description: "Successful MVPs either spin out into independent products, or become modules inside the EZMQ OS." }
  ];

  return (
    <Section className="py-20 md:py-28 grid-pattern" id="model">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-14">
          <span className="section-label mb-4 block">Our Model</span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold">How We Build</h2>
        </div>
        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid md:grid-cols-3 gap-6">
          {steps.map((step, i) => (
            <motion.div key={i} variants={staggerItem} className="card-dark p-7" data-testid={`model-step-${i}`}>
              <span className="number-badge">{step.number}</span>
              <h3 className="font-heading text-lg font-semibold mt-3 mb-2">{step.title}</h3>
              <p className="text-white/50 text-sm leading-relaxed">{step.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </Section>
  );
};

// Enterprise Services Section
const EnterpriseSection = () => {
  const services = [
    { icon: <Cloud size={24} />, title: "Multi-Cloud Strategy", description: "AWS, Azure, Google Cloud, Oracle—we architect and manage across all major platforms" },
    { icon: <Server size={24} />, title: "Infrastructure & DevOps", description: "OpenShift, Kubernetes, HashiCorp stack—modern infrastructure done right" },
    { icon: <Shield size={24} />, title: "Security & Compliance", description: "SOC 2, HIPAA, FedRAMP—built into your architecture from day one" },
    { icon: <Settings size={24} />, title: "Automation & Integration", description: "Connect IT systems to business operations with intelligent automation" },
    { icon: <Users size={24} />, title: "Managed Services", description: "Monitoring, incident response, and continuous optimization" },
    { icon: <FileCheck size={24} />, title: "Lifecycle Management", description: "Planning to decommission—full technical governance" }
  ];

  const technologies = ["AWS", "Azure", "Google Cloud", "Oracle Cloud", "Red Hat OpenShift", "HashiCorp", "Kubernetes", "Terraform"];

  return (
    <Section className="py-20 md:py-28 bg-[#0A0A0A]" id="enterprise">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-start mb-16">
          <div>
            <span className="section-label mb-4 block">Enterprise Consulting</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-5">
              The Same Builders. Enterprise Scale.
            </h2>
            <p className="text-white/60 text-base leading-relaxed mb-6">
              The team that builds our products also consults for enterprises. We bring startup speed 
              and product thinking to large-scale IT challenges—helping organizations modernize, automate, 
              and secure their technical operations while connecting infrastructure to real business outcomes.
            </p>
            <p className="text-white/60 text-base leading-relaxed mb-6">
              We&apos;re not just advisors. We&apos;re practitioners who work with these technologies 
              every day building our own products. That hands-on experience translates directly to 
              solving your enterprise challenges.
            </p>
            <div className="mb-4">
              <p className="text-white/40 text-xs uppercase tracking-wider mb-3">Technologies We Work With</p>
              <div className="flex flex-wrap gap-2">
                {technologies.map((tech, i) => (
                  <span key={i} className="px-3 py-1.5 bg-white/5 border border-white/10 rounded text-xs text-white/70 font-medium">
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid grid-cols-2 gap-4">
            {services.map((service, i) => (
              <motion.div key={i} variants={staggerItem} className="card-dark p-5">
                <div className="text-[#2E5CFF] mb-3">{service.icon}</div>
                <h3 className="font-heading text-sm font-semibold mb-1">{service.title}</h3>
                <p className="text-white/40 text-xs leading-relaxed">{service.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* CTA */}
        <div className="card-dark p-8 border-[#2E5CFF]/20 text-center">
          <h3 className="font-heading text-xl font-bold mb-3">Need Enterprise-Grade Help?</h3>
          <p className="text-white/50 text-sm mb-6 max-w-xl mx-auto">
            Whether it&apos;s cloud migration, security architecture, or full lifecycle management—let&apos;s 
            talk about how we can help your organization move faster.
          </p>
          <a href="#contact" className="btn-primary px-8 py-3 text-sm inline-flex items-center gap-2">
            <MessageSquare size={16} /> Start a Conversation
          </a>
        </div>
      </div>
    </Section>
  );
};

// Focus Areas
const FocusAreasSection = ({ setCurrentPage, setSelectedCategory }) => {
  const areas = [
    { icon: <Brain size={22} />, title: "AI Productivity", description: "AI-powered productivity and reflection tools", category: "AI Productivity" },
    { icon: <Rocket size={22} />, title: "Automation", description: "Human-centered automation systems", category: "Automation" },
    { icon: <Lock size={22} />, title: "Security", description: "Identity protection and secure access systems", category: "Security" },
    { icon: <Cpu size={22} />, title: "IoT", description: "Connected devices and smart environment systems", category: "IoT" },
    { icon: <Database size={22} />, title: "Infrastructure", description: "Data optimization and infrastructure tooling", category: "Infrastructure" },
    { icon: <MessageSquare size={22} />, title: "Social AI", description: "Social and conversational AI experiences", category: "Social AI" },
    { icon: <Heart size={22} />, title: "Wellness + Biometrics", description: "Personal optimization and health tracking platforms", category: "Wellness + Biometrics" }
  ];

  const handleTileClick = (category) => {
    setSelectedCategory(category);
    setCurrentPage("portfolio");
  };

  return (
    <Section className="py-20 md:py-28 bg-[#0A0A0A]" id="focus">
      <div className="max-w-6xl mx-auto px-6">
        <div className="mb-12">
          <span className="section-label mb-4 block">Focus Areas</span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-3">We Chase Leverage, Not Trends</h2>
          <p className="text-white/50 max-w-xl">Our projects span multiple verticals, unified by one rule: reduce friction and increase clarity. Click any area to explore products.</p>
        </div>
        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {areas.map((area, i) => (
            <motion.div 
              key={i} 
              variants={staggerItem} 
              className="card-dark p-6 cursor-pointer group" 
              onClick={() => handleTileClick(area.category)}
              data-testid={`focus-area-${i}`}
            >
              <div className="text-[#2E5CFF] mb-3 group-hover:scale-110 transition-transform">{area.icon}</div>
              <h3 className="font-heading text-base font-semibold mb-1 group-hover:text-[#2E5CFF] transition-colors">{area.title}</h3>
              <p className="text-white/50 text-sm">{area.description}</p>
              <div className="mt-3 flex items-center gap-1 text-[#2E5CFF] text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                View Products <ArrowRight size={12} />
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </Section>
  );
};

// Contact Section with Rainbow Rock mention
const ContactSection = () => {
  const [formData, setFormData] = useState({ name: "", email: "", inquiry_type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const inquiryTypes = [
    { value: "collaboration", label: "Collaboration" },
    { value: "product_mvp", label: "Product / MVP" },
    { value: "partnership", label: "Partnership" },
    { value: "investment", label: "Investment" },
    { value: "other", label: "Other" }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      const res = await axios.post(`${API}/contact`, formData);
      if (res.data.success) {
        setSubmitStatus({ type: "success", message: res.data.message_response });
        setFormData({ name: "", email: "", inquiry_type: "", message: "" });
      }
    } catch (err) {
      setSubmitStatus({ type: "error", message: "Something went wrong. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Section className="py-20 md:py-28 grid-pattern" id="contact">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <span className="section-label mb-4 block">Get in Touch</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-5">Let&apos;s Build Something</h2>
            <p className="text-white/60 text-base mb-6">Whether you want to collaborate, co-create, or explore what&apos;s next—we&apos;re always building.</p>
            
            <div className="card-dark p-5 mb-6">
              <p className="text-white/60 text-sm flex items-center gap-2">
                <ArrowRight size={14} className="text-[#2E5CFF]" />
                Interested in our venture model? Let&apos;s talk.
              </p>
            </div>

            {/* Rainbow Rock ownership */}
            <div className="border-t border-white/10 pt-6 mt-6">
              <p className="font-mono text-xs text-white/40 uppercase tracking-wider mb-2">Owned & Operated By</p>
              <div className="flex items-center gap-2">
                <Sparkles size={16} className="text-[#2E5CFF]" />
                <span className="font-heading text-lg font-semibold">Rainbow Rock</span>
              </div>
              <p className="text-white/40 text-sm mt-1">Cosmic Venture Studio</p>
            </div>
          </div>

          <div className="form-dark p-6 md:p-8" data-testid="contact-form">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="text-xs uppercase tracking-wider text-white/50 mb-1 block">Name</label>
                <input type="text" required value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="Your name" className="w-full p-3 text-sm" data-testid="contact-name-input" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-white/50 mb-1 block">Email</label>
                <input type="email" required value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} placeholder="your@email.com" className="w-full p-3 text-sm" data-testid="contact-email-input" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-white/50 mb-1 block">Inquiry Type</label>
                <Select value={formData.inquiry_type} onValueChange={value => setFormData({ ...formData, inquiry_type: value })}>
                  <SelectTrigger className="w-full bg-black/50 border-white/10 text-white text-sm" data-testid="contact-inquiry-select">
                    <SelectValue placeholder="Select inquiry type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10">
                    {inquiryTypes.map(type => (
                      <SelectItem key={type.value} value={type.value} className="text-white hover:bg-white/5 text-sm">{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-white/50 mb-1 block">Message</label>
                <textarea required rows={4} value={formData.message} onChange={e => setFormData({ ...formData, message: e.target.value })} placeholder="Tell us about your project..." className="w-full p-3 text-sm resize-none" data-testid="contact-message-input" />
              </div>

              <AnimatePresence>
                {submitStatus && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className={`p-3 rounded-lg flex items-center gap-2 ${submitStatus.type === "success" ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-400" : "bg-red-500/10 border border-red-500/30 text-red-400"}`} data-testid="contact-status">
                    {submitStatus.type === "success" ? <Check size={16} /> : <AlertCircle size={16} />}
                    <span className="text-sm">{submitStatus.message}</span>
                  </motion.div>
                )}
              </AnimatePresence>

              <button type="submit" disabled={isSubmitting} className="btn-primary w-full py-3 text-sm flex items-center justify-center gap-2" data-testid="contact-submit-btn">
                {isSubmitting ? "..." : <><Send size={16} /> Send Message</>}
              </button>
            </form>
          </div>
        </div>
      </div>
    </Section>
  );
};

// Footer
const Footer = () => (
  <footer className="py-12 bg-[#0A0A0A] border-t border-white/5" data-testid="footer">
    <div className="max-w-6xl mx-auto px-6">
      <div className="grid md:grid-cols-4 gap-10 mb-10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-2 mb-3">
            <div className="status-indicator"></div>
            <span className="font-heading text-base font-bold">EZMQ</span>
          </div>
          <p className="text-white/50 text-sm mb-4">Venture Studio · Product Incubator · OS Builder</p>
          <div className="flex items-center gap-2 text-white/30 text-xs">
            <Sparkles size={12} />
            <span>A Rainbow Rock Company</span>
          </div>
        </div>
        <div>
          <h4 className="text-xs uppercase tracking-wider text-white/40 mb-3">Navigation</h4>
          <nav className="space-y-2">
            {["Model", "Focus", "Contact"].map(link => (
              <a key={link} href={`#${link.toLowerCase()}`} className="block text-white/50 hover:text-white text-sm">{link}</a>
            ))}
          </nav>
        </div>
        <div>
          <h4 className="text-xs uppercase tracking-wider text-white/40 mb-3">For Investors</h4>
          <p className="text-sm text-white/50 mb-3">Interested in our venture model?</p>
          <a href="#contact" className="inline-flex items-center gap-1 text-[#2E5CFF] hover:underline text-sm" data-testid="footer-investor-cta">
            Let&apos;s Talk <ArrowRight size={14} />
          </a>
        </div>
      </div>
      <div className="pt-6 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-3">
        <p className="text-xs text-white/30">© {new Date().getFullYear()} EZMQ. All rights reserved.</p>
        <p className="text-xs text-white/30">Owned by Rainbow Rock</p>
      </div>
    </div>
  </footer>
);

// Portfolio Page
const PortfolioPage = ({ selectedCategory, setSelectedCategory, setCurrentPage }) => {
  const [products, setProducts] = useState([]);
  const [myBetas, setMyBetas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signingUp, setSigningUp] = useState(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const { user } = useAuth();

  const categories = ["All", "AI Productivity", "Automation", "Security", "IoT", "Infrastructure", "Social AI", "Wellness + Biometrics"];

  const handleContactClick = () => {
    setSelectedCategory(null);
    setCurrentPage("home");
    setTimeout(() => {
      document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes] = await Promise.all([axios.get(`${API}/products`)]);
        setProducts(productsRes.data);
        
        if (user) {
          const token = localStorage.getItem("ezmq_token");
          const betasRes = await axios.get(`${API}/products/my-betas`, { headers: { Authorization: `Bearer ${token}` } });
          setMyBetas(betasRes.data.map(b => b.product_id));
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  const filteredProducts = selectedCategory && selectedCategory !== "All" 
    ? products.filter(p => p.category === selectedCategory)
    : products;

  const handleBetaSignup = async (productId) => {
    if (!user) {
      setAuthModalOpen(true);
      return;
    }
    setSigningUp(productId);
    try {
      const token = localStorage.getItem("ezmq_token");
      await axios.post(`${API}/products/${productId}/beta-signup`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setMyBetas([...myBetas, productId]);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to sign up");
    } finally {
      setSigningUp(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: "status-active",
      beta: "status-beta",
      coming_soon: "status-coming"
    };
    const labels = { active: "Active", beta: "Beta", coming_soon: "Coming Soon" };
    return <span className={`px-2 py-1 rounded text-xs font-mono uppercase ${styles[status]}`}>{labels[status]}</span>;
  };

  const categoryIcons = {
    "AI Productivity": <Brain size={20} />,
    "Automation": <Rocket size={20} />,
    "Security": <Lock size={20} />,
    "IoT": <Cpu size={20} />,
    "Infrastructure": <Database size={20} />,
    "Social AI": <MessageSquare size={20} />,
    "Wellness + Biometrics": <Heart size={20} />
  };

  return (
    <>
      <div className="min-h-screen bg-[#050505] pt-20">
        <div className="max-w-6xl mx-auto px-6 py-12">
          <div className="mb-8">
            <span className="section-label mb-4 block">Product Portfolio</span>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-4">
              {selectedCategory && selectedCategory !== "All" ? selectedCategory : "Our MVPs & Products"}
            </h1>
            <p className="text-white/60 max-w-2xl">
              {selectedCategory && selectedCategory !== "All" 
                ? `Explore our ${selectedCategory} products. Sign up for beta access to get early adopter discounts.`
                : "Explore our portfolio of products in various stages. Sign up for beta access to get early adopter discounts and shape the future of these tools."}
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat === "All" ? null : cat)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  (cat === "All" && !selectedCategory) || selectedCategory === cat
                    ? "bg-[#2E5CFF] text-white"
                    : "bg-white/5 text-white/60 hover:bg-white/10 hover:text-white"
                }`}
                data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Beta Benefits Banner */}
          <div className="card-dark p-6 mb-10 border-[#2E5CFF]/30">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-[#2E5CFF]/20 rounded-lg">
                  <Star size={24} className="text-[#2E5CFF]" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold mb-2">Early Adopter Benefits</h3>
                  <ul className="text-white/60 text-sm space-y-1">
                    <li>• <span className="text-[#2E5CFF]">50% discount</span> on launch pricing</li>
                    <li>• Priority access to new features</li>
                    <li>• Direct line to the product team</li>
                  </ul>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <button 
                  onClick={handleContactClick}
                  className="btn-primary px-6 py-3 text-sm flex items-center justify-center gap-2 whitespace-nowrap"
                  data-testid="schedule-consultation-btn"
                >
                  <MessageSquare size={16} /> Schedule Consultation
                </button>
                <p className="text-white/40 text-xs text-center">Free 30-min strategy call</p>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-20 text-white/50">Loading products...</div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-white/50 mb-4">No products in this category yet.</p>
              <button onClick={() => setSelectedCategory(null)} className="text-[#2E5CFF] hover:underline text-sm">
                View all products
              </button>
            </div>
          ) : (
            <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="grid md:grid-cols-2 gap-6">
              {filteredProducts.map((product) => (
                <motion.div key={product.id} variants={staggerItem} className="card-dark p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-[#2E5CFF]/10 rounded-lg text-[#2E5CFF]">
                        {categoryIcons[product.category] || <Briefcase size={20} />}
                      </div>
                      <div>
                        <h3 className="font-heading text-lg font-semibold">{product.name}</h3>
                        <p className="text-white/40 text-xs">{product.category}</p>
                      </div>
                    </div>
                    {getStatusBadge(product.status)}
                  </div>

                  <p className="text-[#2E5CFF] text-sm font-medium mb-2">{product.tagline}</p>
                  <p className="text-white/50 text-sm mb-4 leading-relaxed">{product.description}</p>

                  <div className="flex flex-wrap gap-2 mb-5">
                    {product.features.slice(0, 3).map((feature, i) => (
                      <span key={i} className="px-2 py-1 bg-white/5 rounded text-xs text-white/60">{feature}</span>
                    ))}
                  </div>

                  {product.beta_available && (
                    <div className="pt-4 border-t border-white/10 flex gap-2">
                      {myBetas.includes(product.id) ? (
                        <div className="flex items-center gap-2 text-emerald-400 text-sm flex-1">
                          <Check size={16} /> You&apos;re on the beta list!
                        </div>
                      ) : (
                        <button
                          onClick={() => handleBetaSignup(product.id)}
                          disabled={signingUp === product.id}
                          className="btn-primary flex-1 py-3 text-sm flex items-center justify-center gap-2"
                          data-testid={`beta-signup-${product.id}`}
                        >
                          {signingUp === product.id ? "Signing up..." : (
                            <>
                              <Clock size={16} /> Get Beta Access
                            </>
                          )}
                        </button>
                      )}
                      <button 
                        onClick={handleContactClick}
                        className="btn-secondary px-4 py-3 text-sm flex items-center justify-center"
                        title="Schedule consultation"
                      >
                        <MessageSquare size={16} />
                      </button>
                    </div>
                  )}
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>

        {/* Footer in portfolio */}
        <Footer />
      </div>
      <AuthModal isOpen={authModalOpen} onClose={() => setAuthModalOpen(false)} initialMode="register" />
    </>
  );
};

// Home Page
const HomePage = ({ setCurrentPage, setSelectedCategory }) => (
  <>
    <HeroSection setCurrentPage={setCurrentPage} />
    <WhatWeDoSection />
    <ModelSection />
    <EnterpriseSection />
    <FocusAreasSection setCurrentPage={setCurrentPage} setSelectedCategory={setSelectedCategory} />
    <ContactSection />
    <Footer />
  </>
);

// Main App
function App() {
  const [currentPage, setCurrentPage] = useState("home");
  const [selectedCategory, setSelectedCategory] = useState(null);

  return (
    <AuthProvider>
      <div className="min-h-screen bg-[#050505]">
        <Header currentPage={currentPage} setCurrentPage={setCurrentPage} />
        {currentPage === "home" ? (
          <HomePage setCurrentPage={setCurrentPage} setSelectedCategory={setSelectedCategory} />
        ) : (
          <PortfolioPage selectedCategory={selectedCategory} setSelectedCategory={setSelectedCategory} setCurrentPage={setCurrentPage} />
        )}
      </div>
    </AuthProvider>
  );
}

export default App;
